import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LegacyJob } from '../types';
import { useCities } from '../src/contexts/CitiesContext';
import { DEPARTMENT_KEY_MAP } from '../types';

type Job = LegacyJob;

interface SearchWithSuggestionsProps {
  jobs: Job[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface Suggestion {
  id: string;
  type: 'job' | 'department' | 'city';
  label: string;
  subtitle?: string;
  count: number;
}

const SearchWithSuggestions: React.FC<SearchWithSuggestionsProps> = ({
  jobs,
  value,
  onChange,
  placeholder
}) => {
  const { t } = useTranslation();
  const { citiesMap } = useCities();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // 获取部门翻译
  const getDepartmentTranslation = useCallback((dept: string) => {
    const key = DEPARTMENT_KEY_MAP[dept];
    if (!key) return dept;
    return t(`jobData.departments.${key}`, dept);
  }, [t]);

  // 获取城市名称
  const getCityName = useCallback((cityId: string) => {
    const city = citiesMap.get(cityId);
    return city ? (t('jobData.currentLang', 'zh').startsWith('zh') ? city.nameZh : city.nameEn) : cityId;
  }, [citiesMap, t]);

  // 生成搜索建议
  useEffect(() => {
    if (value.length >= 2) {
      const results: Suggestion[] = [];
      const seen = new Set<string>();

      // 职位标题匹配
      jobs
        .filter(job =>
          job.title.toLowerCase().includes(value.toLowerCase()) &&
          !seen.has(job.title)
        )
        .slice(0, 3)
        .forEach(job => {
          results.push({
            id: `job-${job.id}`,
            type: 'job',
            label: job.title,
            subtitle: `${getDepartmentTranslation(job.department)} · ${job.location}`,
            count: 1
          });
          seen.add(job.title);
        });

      // 部门匹配
      const departments = [...new Set(jobs.map(j => j.department))];
      departments
        .filter(dept =>
          dept.toLowerCase().includes(value.toLowerCase()) &&
          !seen.has(dept)
        )
        .slice(0, 2)
        .forEach(dept => {
          const count = jobs.filter(j => j.department === dept).length;
          results.push({
            id: `dept-${dept}`,
            type: 'department',
            label: getDepartmentTranslation(dept),
            subtitle: t('fullJobList.filters.departments'),
            count
          });
          seen.add(dept);
        });

      // 城市匹配
      const cityIds = [...new Set(jobs.map(j => j.city).filter(Boolean))];
      cityIds
        .filter(cityId => {
          const city = citiesMap.get(cityId);
          return city && (
            city.nameZh.toLowerCase().includes(value.toLowerCase()) ||
            city.nameEn.toLowerCase().includes(value.toLowerCase())
          ) && !seen.has(cityId);
        })
        .slice(0, 2)
        .forEach(cityId => {
          const count = jobs.filter(j => j.city === cityId).length;
          results.push({
            id: `city-${cityId}`,
            type: 'city',
            label: getCityName(cityId),
            subtitle: t('fullJobList.filters.cities'),
            count
          });
          seen.add(cityId);
        });

      setSuggestions(results);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value, jobs, citiesMap, getDepartmentTranslation, getCityName, t]);

  // 处理建议点击
  const handleSuggestionClick = (suggestion: Suggestion) => {
    onChange(suggestion.label);
    setShowSuggestions(false);
  };

  // 键盘导航
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // 获取建议图标
  const getSuggestionIcon = (type: Suggestion['type']) => {
    switch (type) {
      case 'job':
        return (
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'department':
        return (
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'city':
        return (
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
    }
  };

  return (
    <div className="relative">
      <div className="relative group">
        <div className="absolute inset-y-0 left-4 sm:left-5 flex items-center pointer-events-none">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder || t('fullJobList.searchPlaceholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (value.length >= 2) setShowSuggestions(true);
          }}
          onBlur={() => {
            // 延迟隐藏，以便点击建议项
            setTimeout(() => setShowSuggestions(false), 200);
          }}
          onKeyDown={handleKeyDown}
          className="w-full bg-slate-50 border border-slate-100 rounded-xl pl-11 sm:pl-14 pr-12 sm:pr-14 py-3 sm:py-4 text-sm sm:text-base font-bold placeholder:text-slate-300 shadow-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500/20 transition-all outline-none"
        />
        {value && (
          <button
            onClick={() => onChange('')}
            className="absolute inset-y-0 right-4 sm:right-5 flex items-center text-slate-300 hover:text-slate-500 transition-colors"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* 搜索建议下拉框 */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-left px-4 sm:px-5 py-3 sm:py-3.5 flex items-start gap-3 transition-colors ${
                index === selectedIndex
                  ? 'bg-blue-50'
                  : 'hover:bg-slate-50'
              }`}
            >
              <div className="shrink-0 mt-0.5">
                {getSuggestionIcon(suggestion.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-900 truncate">
                    {suggestion.label}
                  </span>
                  <span className="text-xs text-slate-400 shrink-0">
                    {suggestion.count}
                  </span>
                </div>
                {suggestion.subtitle && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {suggestion.subtitle}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchWithSuggestions;
